import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLang } from "@/i18n";
import { api, formatApiError } from "@/api";
import { SectionHeading } from "@/components/Reveal";

const inputCls =
  "w-full bg-obsidian-surface border border-slate-700 rounded-sm px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/70 focus:ring-1 focus:ring-gold/30 transition-[border-color,box-shadow] duration-300";

export default function ContactSection() {
  const { t } = useLang();
  const [form, setForm] = useState({
    intent: "present_project", role: "african_institution",
    name: "", organization: "", email: "", country: "", sector: "", message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm((f) => ({ ...f, name: "", organization: "", email: "", country: "", sector: "", message: "" }));
    } catch (err) {
      toast.error(formatApiError(err) || t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const intents = [
    ["present_project", t.contact.intentProject],
    ["explore_opportunity", t.contact.intentOpportunity],
    ["access_request", t.contact.intentAccess],
  ];
  const roles = [
    ["african_institution", t.contact.roleAfrica],
    ["international_actor", t.contact.roleWorld],
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 bg-obsidian-surface" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-14 lg:gap-20">
          <div>
            <SectionHeading overline={t.contact.overline} title={t.contact.title} text={t.contact.text} testid="contact-title" />
          </div>

          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-3">{t.contact.intentProject} / {t.contact.intentOpportunity}</p>
              <div className="flex flex-wrap gap-2" data-testid="contact-intent-group">
                {intents.map(([v, label]) => (
                  <button type="button" key={v} data-testid={`contact-intent-${v}`} onClick={() => setForm((f) => ({ ...f, intent: v }))}
                    className={`px-4 py-2 text-xs rounded-full border transition-colors duration-300 ${form.intent === v ? "border-gold bg-gold/10 text-gold" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2" data-testid="contact-role-group">
              {roles.map(([v, label]) => (
                <button type="button" key={v} data-testid={`contact-role-${v}`} onClick={() => setForm((f) => ({ ...f, role: v }))}
                  className={`flex-1 px-4 py-3 text-xs font-mono uppercase tracking-[0.14em] rounded-sm border transition-colors duration-300 ${form.role === v ? "border-gold bg-gold/10 text-gold" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required data-testid="contact-input-name" value={form.name} onChange={set("name")} placeholder={t.contact.name} className={inputCls} />
              <input required data-testid="contact-input-organization" value={form.organization} onChange={set("organization")} placeholder={t.contact.organization} className={inputCls} />
              <input required type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} placeholder={t.contact.email} className={inputCls} />
              <input data-testid="contact-input-country" value={form.country} onChange={set("country")} placeholder={t.contact.country} className={inputCls} />
            </div>
            <input data-testid="contact-input-sector" value={form.sector} onChange={set("sector")} placeholder={t.contact.sector} className={inputCls} />
            <textarea required rows={5} data-testid="contact-input-message" value={form.message} onChange={set("message")} placeholder={t.contact.messagePlaceholder} className={`${inputCls} resize-none`} />

            <button type="submit" disabled={sending} data-testid="contact-submit"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-obsidian text-sm font-medium tracking-wide rounded-full hover:bg-gold-light disabled:opacity-60 transition-colors duration-300">
              {sending ? t.contact.sending : t.contact.submit}
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
